import React, { memo, useContext, useState, Fragment } from 'react';
import { TransactionsContext } from '../context/transactions.context';
import { ThisMonthBudgetContext } from '../context/budgets.context';
import { calcExpInc, formatAmount, calcBarPercent } from '../helpers';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import clsx from 'clsx';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import SelectedMonth from './SelectedMonth';
import LinearProgress from '@material-ui/core/LinearProgress';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import EditIcon from '@material-ui/icons/Edit';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import PlannedIncForm from './PlannedIncForm';
import useStyles from './styles/budgetSummary.styles';
import MonthBudgetForm from './MonthBudgetForm';
import Tooltip from '@material-ui/core/Tooltip';
import Tip from './Tip';
import { CurrencyContext } from '../context/currency.context';

function BudgetSummary({ tipOpen, handleNextTip, tips }) {
	const classes = useStyles();
	const [ anchorEl, setAnchorEl ] = useState(null);
	const popoverOpen = Boolean(anchorEl);
	const id = popoverOpen ? 'simple-popover' : undefined;
	const [ plannedIncDialogOpen, setPlannedIncDialogOpen ] = useState(false);
	const [ monthBudgetDialog, setMonthBudgetDialog ] = useState(false);
	const transactions = useContext(TransactionsContext);
	const thisMonthBudget = useContext(ThisMonthBudgetContext);
	const currency = useContext(CurrencyContext);
	const up600 = useMediaQuery('(min-width:600px)');
	const heightUp650 = useMediaQuery('(min-height:650px)');

	// ---- actual ----
	const [ actualExp, actualInc ] = calcExpInc(transactions);
	const totalActual = actualExp + actualInc;

	// ---- planned ----
	const plannedInc = !thisMonthBudget ? 0 : thisMonthBudget.plannedInc;
	const plannedExp = !thisMonthBudget
		? 0
		: Object.values(thisMonthBudget.allBudgets).reduce((acc, curr) => acc + curr.planned, 0) *
			-1;
	const totalPlanned = plannedInc + plannedExp;

	const percentage = parseInt(totalActual / totalPlanned * 100);

	// ---- Progress bar percentages ----
	const overallProgressBar =
		!plannedInc || percentage < 1 ? 0 : (percentage > 100 && 100) || percentage;

	return (
		<Fragment>
			<Popover
				id={id}
				open={popoverOpen}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}>
				<div className={classes.morePopover}>
					<Button
						variant="contained"
						color="primary"
						onClick={() => {
							setMonthBudgetDialog(true);
							setAnchorEl(null);
						}}>
						Reset this months's budgets
					</Button>
				</div>
			</Popover>
			<PlannedIncForm
				plannedInc={plannedInc}
				dialogOpen={plannedIncDialogOpen}
				setDialogOpen={setPlannedIncDialogOpen}
			/>
			<MonthBudgetForm dialogOpen={monthBudgetDialog} setDialogOpen={setMonthBudgetDialog} />
			<Tip
				buttonTop={heightUp650 || !up600}
				title={tips[6]}
				open={tipOpen[6]}
				badge={`7/${tips.length}`}
				placement={heightUp650 ? 'top' : 'bottom'}
				buttonLabel="got it"
				handleClose={handleNextTip(6)}>
				<Paper className={classes.root}>
					<LinearProgress
						variant="determinate"
						className={classes.overallBar}
						value={overallProgressBar}
					/>
					<List className={classes.list}>
						<div className={classes.monthContainer}>
							<div className={classes.flexGrow}>
								<SelectedMonth />
							</div>
							{!!thisMonthBudget && (
								<Tooltip title="Reset Budgets" placement="top" arrow>
									<IconButton
										onClick={e => setAnchorEl(e.currentTarget)}
										className={classes.moreButton}
										aria-label="more options">
										<MoreHorizIcon fontSize="large" />
									</IconButton>
								</Tooltip>
							)}
						</div>
						<Divider />
						<Collapse in={!!thisMonthBudget} timeout={700}>
							{!up600 && (
								<ListItem className={classes.listItemBar}>
									<LinearProgress
										variant="determinate"
										className={classes.bar}
										value={
											plannedInc >= actualInc ? (
												calcBarPercent(actualInc, plannedInc)
											) : (
												0
											)
										}
									/>
								</ListItem>
							)}
							<ListItem
								className={up600 ? classes.listItemTop : classes.listItemMiddle}>
								<ListItemText
									className={classes.itemTextLeft}
									primary={'Planned Income'}
								/>
								{up600 && (
									<LinearProgress
										variant="determinate"
										className={classes.bar}
										value={calcBarPercent(plannedInc, actualInc)}
									/>
								)}
								<ListItemText
									className={classes.itemTextRight}
									primary={
										plannedInc > 0 ? (
											<span className={classes.plannedIncomeWrapper}>
												<Tooltip
													title="Edit Planned Income"
													placement="top"
													arrow>
													<IconButton
														onClick={() =>
															setPlannedIncDialogOpen(true)}>
														<EditIcon />
													</IconButton>
												</Tooltip>
												<span>{formatAmount(plannedInc, currency, 0)}</span>
											</span>
										) : (
											<Button
												variant="contained"
												color="primary"
												size="small"
												onClick={() => setPlannedIncDialogOpen(true)}>
												Plan
											</Button>
										)
									}
								/>
							</ListItem>
							<ListItem className={classes.listItemBottom}>
								<ListItemText
									className={classes.itemTextLeft}
									primary={'Actual Income'}
								/>
								{up600 && (
									<LinearProgress
										variant="determinate"
										className={classes.bar}
										value={calcBarPercent(actualInc, plannedInc)}
									/>
								)}
								<ListItemText
									className={classes.itemTextRight}
									primary={formatAmount(actualInc, currency, 0)}
								/>
							</ListItem>
							<Divider variant="middle" />
							{!up600 && (
								<ListItem className={classes.listItemBar}>
									<LinearProgress
										variant="determinate"
										color="secondary"
										className={classes.bar}
										value={
											plannedExp * -1 >= actualExp * -1 ? (
												calcBarPercent(actualExp, plannedExp)
											) : (
												0
											)
										}
									/>
								</ListItem>
							)}
							<ListItem
								className={up600 ? classes.listItemTop : classes.listItemMiddle}>
								<ListItemText
									className={classes.itemTextLeft}
									primary={'Planned Expenses'}
								/>
								{up600 && (
									<LinearProgress
										variant="determinate"
										color="secondary"
										className={classes.bar}
										value={calcBarPercent(plannedExp, actualExp)}
									/>
								)}
								<ListItemText
									className={classes.itemTextRight}
									primary={formatAmount(plannedExp, currency, 0)}
								/>
							</ListItem>
							<ListItem className={classes.listItemBottom}>
								<ListItemText
									className={classes.itemTextLeft}
									primary={'Actual Expenses'}
								/>
								{up600 && (
									<LinearProgress
										variant="determinate"
										color="secondary"
										className={classes.bar}
										value={calcBarPercent(actualExp, plannedExp)}
									/>
								)}
								<ListItemText
									className={classes.itemTextRight}
									primary={formatAmount(actualExp, currency, 0)}
								/>
							</ListItem>
							<Divider variant="middle" />
							<ListItem className={classes.listItemTotal}>
								<ListItemText
									className={clsx(classes.itemTextLeft, classes.totalItemText)}
									primary="Planning to save"
									secondary={formatAmount(totalPlanned, currency, 0)}
								/>

								<ListItemText
									className={clsx(classes.itemTextRight, classes.totalItemText)}
									primary="Saved"
									secondary={
										formatAmount(totalActual, currency, 0) +
										(totalPlanned > 0 ? ` (${percentage}%)` : '')
									}
								/>
							</ListItem>
						</Collapse>
						<Collapse in={!thisMonthBudget} timeout={700}>
							<div className={classes.setupBudgetsButton}>
								<Button
									variant="contained"
									color="primary"
									onClick={() => setMonthBudgetDialog(true)}>
									Setup this month's budgets
								</Button>
							</div>
						</Collapse>
					</List>
				</Paper>
			</Tip>
		</Fragment>
	);
}

export default memo(BudgetSummary);
